import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Save, Add } from '@mui/icons-material';

const ContentManagementPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Terms & Conditions State
  const [termsEnglish, setTermsEnglish] = useState('');
  const [termsArabic, setTermsArabic] = useState('');
  
  // Cancellation Policy State
  const [cancellationEnglish, setCancellationEnglish] = useState('');
  const [cancellationArabic, setCancellationArabic] = useState('');
  
  // Pricing Rules State
  const [pricingRules, setPricingRules] = useState([
    { name: 'Ramadan Peak Season', type: 'percentage', value: 30, startDate: '2025-03-01', endDate: '2025-03-30' },
    { name: 'Hajj Peak Season', type: 'percentage', value: 50, startDate: '2025-06-05', endDate: '2025-06-20' },
    { name: 'Summer Off-Peak', type: 'percentage', value: -15, startDate: '2025-07-01', endDate: '2025-09-30' },
    { name: 'Group Discount 10+', type: 'percentage', value: -10, minTravelers: 10 },
  ]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSaveTerms = () => {
    // In real implementation, this would call an API
    console.log('Saving terms & conditions:', { termsEnglish, termsArabic });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleSaveCancellation = () => {
    // In real implementation, this would call an API
    console.log('Saving cancellation policy:', { cancellationEnglish, cancellationArabic });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleSavePricingRules = () => {
    // In real implementation, this would call an API
    console.log('Saving pricing rules:', pricingRules);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const addPricingRule = () => {
    setPricingRules([
      ...pricingRules,
      { name: '', type: 'percentage', value: 0, startDate: '', endDate: '' }
    ]);
  };

  const updatePricingRule = (index, field, value) => {
    const updated = [...pricingRules];
    updated[index][field] = value;
    setPricingRules(updated);
  };

  const removePricingRule = (index) => {
    setPricingRules(pricingRules.filter((_, i) => i !== index));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Content Management
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage terms & conditions, cancellation policies, and pricing rules for your Umrah packages.
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Changes saved successfully!
        </Alert>
      )}

      <Paper sx={{ mt: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Terms & Conditions" />
          <Tab label="Cancellation Policy" />
          <Tab label="Pricing Rules" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Terms & Conditions Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Terms & Conditions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define the terms and conditions that apply to all package bookings.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Terms & Conditions (English)"
                    multiline
                    rows={12}
                    value={termsEnglish}
                    onChange={(e) => setTermsEnglish(e.target.value)}
                    placeholder="Payment must be confirmed before departure. Package includes visa processing, accommodation, daily breakfast, airport transfers, and guided ziyarat tours..."
                    helperText="These terms will be displayed on package details and booking confirmation pages"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Terms & Conditions (Arabic)"
                    multiline
                    rows={12}
                    value={termsArabic}
                    onChange={(e) => setTermsArabic(e.target.value)}
                    placeholder="يجب تأكيد الدفع قبل المغادرة. تشمل الباقة معالجة التأشيرة..."
                    helperText="Arabic translation for bilingual support"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveTerms}
                >
                  Save Terms & Conditions
                </Button>
              </Box>
            </Box>
          )}

          {/* Cancellation Policy Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Cancellation Policy
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Define the cancellation policy and refund rules for bookings.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cancellation Policy (English)"
                    multiline
                    rows={12}
                    value={cancellationEnglish}
                    onChange={(e) => setCancellationEnglish(e.target.value)}
                    placeholder="Cancellation 45+ days before departure: 90% refund. 30-44 days: 60% refund. 15-29 days: 30% refund..."
                    helperText="Clear refund structure based on cancellation timeline"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cancellation Policy (Arabic)"
                    multiline
                    rows={12}
                    value={cancellationArabic}
                    onChange={(e) => setCancellationArabic(e.target.value)}
                    placeholder="الإلغاء قبل 45+ يومًا من المغادرة: استرداد 90٪..."
                    helperText="Arabic translation for bilingual support"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveCancellation}
                >
                  Save Cancellation Policy
                </Button>
              </Box>
            </Box>
          )}

          {/* Pricing Rules Tab */}
          {activeTab === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">
                    Pricing Rules
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage seasonal pricing, peak seasons, and discounts
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addPricingRule}
                >
                  Add Rule
                </Button>
              </Box>

              {pricingRules.map((rule, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Rule Name"
                          value={rule.name}
                          onChange={(e) => updatePricingRule(index, 'name', e.target.value)}
                          placeholder="e.g., Ramadan Peak Season"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Type"
                          select
                          value={rule.type}
                          onChange={(e) => updatePricingRule(index, 'type', e.target.value)}
                          SelectProps={{ native: true }}
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Value"
                          type="number"
                          value={rule.value}
                          onChange={(e) => updatePricingRule(index, 'value', parseFloat(e.target.value))}
                          helperText={rule.type === 'percentage' ? '%' : '$'}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Start Date"
                          type="date"
                          value={rule.startDate || ''}
                          onChange={(e) => updatePricingRule(index, 'startDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="End Date"
                          type="date"
                          value={rule.endDate || ''}
                          onChange={(e) => updatePricingRule(index, 'endDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <Button
                          fullWidth
                          color="error"
                          onClick={() => removePricingRule(index)}
                          sx={{ height: '56px' }}
                        >
                          Delete
                        </Button>
                      </Grid>
                    </Grid>
                    
                    {rule.minTravelers && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          label="Min Travelers"
                          type="number"
                          value={rule.minTravelers}
                          onChange={(e) => updatePricingRule(index, 'minTravelers', parseInt(e.target.value))}
                          helperText="For group discounts"
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}

              <Divider sx={{ my: 3 }} />

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Pricing Rule Guidelines:
                </Typography>
                <Typography variant="body2" component="div">
                  • Positive values increase price (e.g., +30% for Ramadan)<br />
                  • Negative values decrease price (e.g., -15% for off-peak)<br />
                  • Date ranges apply seasonal pricing<br />
                  • Min/Max travelers apply group discounts<br />
                  • Multiple rules can apply simultaneously
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSavePricingRules}
                >
                  Save Pricing Rules
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ContentManagementPage;
